import { captureException } from '@sentry/browser';
import ExcelJS, { Worksheet } from 'exceljs';
import FileSaver from 'file-saver';
import { isNumber } from 'lodash';
import { formatDigits } from 'utils/numbers';

export enum SCEligibility {
  contributing = 'Y',
  notContributing = 'N',
  eligible = 'EL',
  nonEligible = 'N/EL',
}
export interface RowFormat {
  activityName?: string;
  code?: string;
  eligiblePct?: number;
  alignedAbsolute?: number;
  alignedPct?: number;
  mitigationSC?: {
    financial?: number;
    eligibility?: SCEligibility;
    doubleCountedFinancial?: number;
  };
  adaptationSC?: {
    financial?: number;
    eligibility?: SCEligibility;
    doubleCountedFinancial?: number;
  };
  waterSC?: { financial?: number; eligibility?: SCEligibility; doubleCountedFinancial?: number };
  pollutionSC?: {
    financial?: number;
    eligibility?: SCEligibility;
    doubleCountedFinancial?: number;
  };
  circularSC?: { financial?: number; eligibility?: SCEligibility; doubleCountedFinancial?: number };
  biodiversitySC?: {
    financial?: number;
    eligibility?: SCEligibility;
    doubleCountedFinancial?: number;
  };
  enabling?: number;
  transitional?: number;
  alignedPctOfEligible?: number;
}

export interface KpiRowFormat {
  kpiName: string;
  total: number;
  eligiblePct: number;
  alignedAbsolute: number;
  alignedPct: number;
  mitigationSC?: number;
  adaptationSC?: number;
  waterSC?: number;
  pollutionSC?: number;
  circularSC?: number;
  biodiversitySC?: number;
  enablingPct: number;
  transitionalPct: number;
  notMaterialPct: number;
  previousYearAlignedAbsolute?: number;
  previousYearAlignedPct?: number;
}

export interface ActivitiesSheetFormat {
  activities: RowFormat[];
  alignedSum: RowFormat;
  total: RowFormat;
}
export interface KpiSheetFormat {
  turnover: KpiRowFormat;
  capex: KpiRowFormat;
  opex: KpiRowFormat;
}

export interface TaxonomyTableData {
  companyName: string;
  reportingYear: number;
  currency: string;
  turnover: ActivitiesSheetFormat;
  capex: ActivitiesSheetFormat;
  opex: ActivitiesSheetFormat;
  kpiTable: KpiSheetFormat;
}

const formatToPercentage = (value?: number) => {
  if (value === undefined || value === null) return ' ';
  return (value * 100).toFixed(2) + '%';
};

const formatSubstantialContribution = (value?: number) => {
  if (value === 0) {
    return undefined;
  } else {
    return formatToPercentage(value);
  }
};

const formatRow = (rowData: RowFormat) => {
  const formattedRow = { ...rowData };

  Object.keys(rowData).forEach((key) => {
    const value = rowData[key as keyof RowFormat];

    if (typeof value === 'object') {
      (formattedRow[key as keyof RowFormat] as string) = value?.doubleCountedFinancial
        ? formatToPercentage(value?.doubleCountedFinancial)
        : ' ';
    } else if (key === 'enabling' || key === 'transitional') {
      if (value === 0 || value === undefined) {
        formattedRow[key as keyof RowFormat] = undefined;
      } else {
        const pctValue = formatToPercentage(value as number);
        (formattedRow[key as keyof RowFormat] as string) =
          key === 'enabling' ? `E (${pctValue})` : `T (${pctValue})`;
      }
    } else if (key === 'alignedAbsolute') {
      (formattedRow[key as keyof RowFormat] as string) = formatDigits(
        value as number,
        0
      )?.toString();
    } else if (isNumber(value)) {
      (formattedRow[key as keyof RowFormat] as string) = formatToPercentage(value);
    } else if (typeof value === 'boolean') {
      (formattedRow[key as keyof RowFormat] as string) = value ? 'Y' : 'N';
    }
  });

  return formattedRow;
};

enum KpiNameEnum {
  turnover = 'Turnover',
  capex = 'CapEx',
  opex = 'OpEx',
}

const formatKpiRow = (rowData: KpiRowFormat) => {
  const formattedRow = { ...rowData };

  Object.keys(rowData).forEach((key) => {
    const value = rowData[key as keyof KpiRowFormat];

    if (key === 'kpiName') {
      const properName = KpiNameEnum[value as keyof typeof KpiNameEnum];
      (formattedRow[key as keyof KpiRowFormat] as string) = properName ?? value;
    }

    if (key === 'alignedAbsolute' || key === 'total') {
      (formattedRow[key as keyof KpiRowFormat] as string) = formatDigits(
        value as number,
        0
      )?.toString();
    } else if (isNumber(value)) {
      (formattedRow[key as keyof KpiRowFormat] as string) = formatToPercentage(value);
    }
  });

  return formattedRow;
};

function setFinancialYear(sheet: ExcelJS.Worksheet, reportingYear: number) {
  if (sheet.name.toLowerCase() === 'template 1') {
    sheet.getRow(4).getCell('C').value = reportingYear;
  } else {
    sheet.getRow(5).getCell('C').value = reportingYear;
  }
}

function setCurrency(sheet: ExcelJS.Worksheet, currency: string) {
  if (sheet.name.toLowerCase() === 'template 1') {
    sheet.getRow(7).getCell('C').value = currency;
    sheet.getRow(7).getCell('E').value = currency;
    sheet.getRow(7).getCell('P').value = currency;
  } else {
    sheet.getRow(8).getCell('E').value = currency;
  }
}

const generateRegularTaxonomyTable = async (data: TaxonomyTableData, sheet: Worksheet) => {
  const COLUMN_HEADERS = [
    { key: 'activityName', letter: 'B' },
    { key: 'code', letter: 'C' },
    { key: 'eligiblePct', letter: 'D' },
    { key: 'alignedAbsolute', letter: 'E' },
    { key: 'alignedPct', letter: 'F' },
    { key: 'mitigationSC', letter: 'G' },
    { key: 'adaptationSC', letter: 'H' },
    { key: 'waterSC', letter: 'I' },
    { key: 'circularSC', letter: 'J' },
    { key: 'pollutionSC', letter: 'K' },
    { key: 'biodiversitySC', letter: 'L' },
    { key: 'enabling', letter: 'M' },
    { key: 'transitional', letter: 'N' },
    { key: 'alignedPctOfEligible', letter: 'O' },
  ];

  const sheetKey = sheet.name.toLowerCase().replace(/^template 2 - /, '');
  const sheetData: ActivitiesSheetFormat = (data as any)[sheetKey];

  // Set column header keys
  COLUMN_HEADERS.forEach((header) => {
    sheet.getColumn(header.letter).key = header.key;
  });

  setFinancialYear(sheet, data.reportingYear);
  setCurrency(sheet, data.currency);

  //   // ''' Dynamic rows ''' //

  // Insert eligible activities rows
  const activityRowIndex = 9;
  sheet.insertRows(
    activityRowIndex + 1,
    sheetData.activities.map((row: RowFormat) => formatRow(row)),
    'i'
  );

  // Delete the sample row
  // Note: This row is provided in the template to inherit styling to added rows below it.
  // It is then removed after the rows are inserted.
  sheet.spliceRows(9, 1);

  const alignedSumRow = 9 + sheetData.activities.length;
  sheet.getRow(alignedSumRow).getCell('C').value = undefined;
  sheet.getRow(alignedSumRow).getCell('C').merge(sheet.getRow(alignedSumRow).getCell('B'));
  sheet.getRow(alignedSumRow).getCell('G').value = formatSubstantialContribution(
    sheetData.alignedSum.mitigationSC?.financial
  );
  sheet.getRow(alignedSumRow).getCell('H').value = formatSubstantialContribution(
    sheetData.alignedSum.adaptationSC?.financial
  );
  sheet.getRow(alignedSumRow).getCell('I').value = formatSubstantialContribution(
    sheetData.alignedSum.waterSC?.financial
  );
  sheet.getRow(alignedSumRow).getCell('J').value = formatSubstantialContribution(
    sheetData.alignedSum.circularSC?.financial
  );
  sheet.getRow(alignedSumRow).getCell('K').value = formatSubstantialContribution(
    sheetData.alignedSum.pollutionSC?.financial
  );
  sheet.getRow(alignedSumRow).getCell('L').value = formatSubstantialContribution(
    sheetData.alignedSum.biodiversitySC?.financial
  );

  const totalRow = alignedSumRow + 1;
  sheet.getRow(totalRow).getCell('C').value = undefined;
  sheet.getRow(totalRow).getCell('C').merge(sheet.getRow(totalRow).getCell('B'));
  sheet.getRow(totalRow).getCell('D').value = formatToPercentage(sheetData.total.eligiblePct);
  sheet.getRow(totalRow).getCell('E').value = sheetData.total.alignedAbsolute;
  sheet.getRow(totalRow).getCell('F').value = formatToPercentage(sheetData.total.alignedPct);
  sheet.getRow(totalRow).getCell('G').value = formatSubstantialContribution(
    sheetData.total.mitigationSC?.financial
  );
  sheet.getRow(totalRow).getCell('H').value = formatSubstantialContribution(
    sheetData.total.adaptationSC?.financial
  );
  sheet.getRow(totalRow).getCell('I').value = formatSubstantialContribution(
    sheetData.total.waterSC?.financial
  );
  sheet.getRow(totalRow).getCell('J').value = formatSubstantialContribution(
    sheetData.total.circularSC?.financial
  );
  sheet.getRow(totalRow).getCell('K').value = formatSubstantialContribution(
    sheetData.total.pollutionSC?.financial
  );
  sheet.getRow(totalRow).getCell('L').value = formatSubstantialContribution(
    sheetData.total.biodiversitySC?.financial
  );
  sheet.getRow(totalRow).getCell('M').value = formatToPercentage(
    sheetData.total.enabling as number
  );
  sheet.getRow(totalRow).getCell('N').value = formatToPercentage(
    sheetData.total.transitional as number
  );
  sheet.getRow(totalRow).getCell('O').value = formatToPercentage(
    sheetData.total.alignedPctOfEligible
  );
};

const generateKpiTable = (tableData: TaxonomyTableData, sheet: Worksheet) => {
  const COLUMN_HEADERS = [
    { key: 'kpiName', letter: 'B' },
    { key: 'total', letter: 'C' },
    { key: 'eligiblePct', letter: 'D' },
    { key: 'alignedAbsolute', letter: 'E' },
    { key: 'alignedPct', letter: 'F' },
    { key: 'mitigationSC', letter: 'G' },
    { key: 'adaptationSC', letter: 'H' },
    { key: 'waterSC', letter: 'I' },
    { key: 'circularSC', letter: 'J' },
    { key: 'pollutionSC', letter: 'K' },
    { key: 'biodiversitySC', letter: 'L' },
    { key: 'enablingPct', letter: 'M' },
    { key: 'transitionalPct', letter: 'N' },
    { key: 'notMaterialPct', letter: 'O' },
    { key: 'previousYearAlignedAbsolute', letter: 'P' },
    { key: 'previousYearAlignedPct', letter: 'Q' },
  ];

  const sheetData: KpiSheetFormat = tableData.kpiTable;

  // Set column header keys
  COLUMN_HEADERS.forEach((header) => {
    sheet.getColumn(header.letter).key = header.key;
  });

  setFinancialYear(sheet, tableData.reportingYear);
  setCurrency(sheet, tableData.currency);

  const formattedRows = [sheetData.turnover, sheetData.opex, sheetData.capex].map((row) =>
    formatKpiRow(row)
  );

  sheet.insertRows(11, formattedRows);

  // Delete the sample rows
  // Note: These rows are provided in the template to inherit styling to added rows below it.
  // They are then removed after the rows are inserted.
  sheet.spliceRows(8, 3);

  sheet.getRows(8, 3)?.forEach((row, index) => {
    for (let i = 1; i <= 17; i++) {
      const cell = row.getCell(i);

      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' },
      };
    }
  });
};

export const generateTaxonomyTable = async (data: TaxonomyTableData) => {
  try {
    const workbook = new ExcelJS.Workbook();
    const templateFile = await fetch('/assets/taxonomy_table/template.xlsx');
    const templateBuffer = await templateFile.arrayBuffer();

    await workbook.xlsx.load(templateBuffer);

    workbook.eachSheet((sheet) => {
      const s = sheet.name.toLowerCase();

      if (s === 'template 1') {
        generateKpiTable(data, sheet);
      } else if (s.includes('turnover') || s.includes('capex') || s.includes('opex')) {
        generateRegularTaxonomyTable(data, sheet);
      }
    });

    // exports workbook
    workbook.xlsx.writeBuffer().then((buffer) => {
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      FileSaver.saveAs(blob, `${data?.companyName}_${data.reportingYear}_Taxonomy_Table.xlsx`);
    });
  } catch (error) {
    captureException(error);
  }
};
